/* eslint-disable sort-keys */
import { Clickable, P } from '@ahmdigital/ui';
import getOpeningHours from '@ahmdigital/logic/lib/utils/get-opening-hours';

import { LIFE_INSURANCE } from '../../../../models/life-insurance/constants';
import constants from '../../../../ahm-constants';

const EFFECTIVE_DATE_JULY_2023 = '04/07/2023';
const EFFECTIVE_DATE_MARCH_2024 = '01/03/2024';
const TMD_EFFECTIVE_DATE_MARCH_2026 = '24/03/2026';
const FSG_EFFECTIVE_DATE_JUNE_2026 = '22/06/2026';
const FSG_JUNE_2026 = 'https://static.ahm.com.au/files/cash-boost/fsg-june-2026.pdf';

const lifeAndIncomeContent = {
  meta: {
    title: 'PDS and Useful Documents - ahm Life Insurance and Income Protection',
    description: 'Need help? Get in touch with us about ahm life insurance.',
  },
  banner: {
    title: 'Useful documents',
    preHeading: 'ahm Life Insurance and Income Protection Insurance',
  },
  content: [
    {
      heading: 'For policies purchased between 1 July 2023 - 28 February 2024 and start with ‘AL’',
    },
    {
      heading: 'Product Disclosure Statements',
      body: [
        'The Product Disclosure Statement provides detail on the key features, benefits, exclusions, terms and conditions of the relevant product.',

        'Applies to policies purchased between 1 July 2023 - 28 February 2024 and start with ‘AL’.',

        'Important Notice: Effective 20 December 2024, policies previously issued by Integrity Life Australia (Integrity) have now transferred to Zurich Australia Limited (Zurich).',
        {
          children: [
            <Clickable to={constants.URLS.FILES.LIFE_INSURANCE_PRODUCT_DISCLOSURE_JULY}>
              ahm Life Insurance Product Disclosure Statement (effective {EFFECTIVE_DATE_JULY_2023})
            </Clickable>,
            <Clickable to={constants.URLS.FILES.LIFE_INSURANCE_INCOME_PROTECTION_PRODUCT_DISCLOSURE}>
              ahm Income Protection Product Disclosure Statement (effective {EFFECTIVE_DATE_JULY_2023})
            </Clickable>,
          ],
        },
        'References made in the above policy’s PDS to:',
        {
          children: [
            '’Integrity Life Australia Limited ABN 83 089 981 73 AFSL no. 245492’',
            '’Integrity Life’ and',
            '’we’, ’us’, ’our’, or ’insurer’',
            'should now be read as ’Zurich Australia Limited ABN 92 000 010 195, AFSL 232510’',
          ],
        },
        <>
          Zurich encourages customers to read Zurich’s{' '}
          <Clickable to={constants.URLS.THIRD_PARTY.ZURICH_LIFE_PRIVACY_POLICY}>privacy policy</Clickable> to understand
          how Zurich collects, holds, uses and discloses their personal information.
        </>,
      ],
    },
    {
      heading: 'For policies purchased from 1 March 2024 and start with ’AZ’.',
    },
    {
      heading: 'Product Disclosure Statements',
      body: [
        'The Product Disclosure Statement provides detail on the key features, benefits, exclusions, terms and conditions of the relevant product.',

        'For policies starting with ‘AZ’.',
        {
          children: [
            <Clickable to={constants.URLS.THIRD_PARTY.LIFE_INSURANCE_PDS_ZURICH}>
              ahm Life Insurance Product Disclosure Statement (effective {EFFECTIVE_DATE_MARCH_2024})
            </Clickable>,
            <Clickable to={constants.URLS.THIRD_PARTY.INCOME_INSURANCE_PDS_ZURICH}>
              ahm Income Protection Product Disclosure Statement (effective {EFFECTIVE_DATE_MARCH_2024})
            </Clickable>,
          ],
        },
      ],
    },
    {
      heading: 'For all policies purchased between 1 July 2023 and now:',
    },
    {
      heading: 'Target Market Determination',
      body: [
        {
          children: [
            <Clickable to={constants.URLS.THIRD_PARTY.LIFE_INSURANCE_TMD_ZURICH}>
              ahm Life Insurance Target Market Determination (effective {TMD_EFFECTIVE_DATE_MARCH_2026})
            </Clickable>,
            <Clickable to={constants.URLS.THIRD_PARTY.INCOME_INSURANCE_TMD_ZURICH}>
              ahm Income Protection Target Market Determination (effective {TMD_EFFECTIVE_DATE_MARCH_2026})
            </Clickable>,
          ],
        },
      ],
    },
    {
      heading: 'Financial Services Guide',
      body: [
        'The Financial Services Guide contains information about who is providing you with the financial services. It explains the financial service offered, the fees charged and how we will deal with complaints.',
        {
          children: [
            <Clickable to={FSG_JUNE_2026}>
              ahm Life Insurance Financial Services Guide (effective {FSG_EFFECTIVE_DATE_JUNE_2026})
            </Clickable>,
          ],
        },
        <P looksLike="em">
          From 22 June 2026, the Financial Services Guide effective 22 June 2026 will apply to all policies. For ahm
          life insurance policies that transferred from Integrity Life Australia Limited to Zurich Australia Limited,
          upon the transfer NEOS Admin Pty Ltd received a fee under that Financial Service Guide as if they were new
          policies sold. This does not affect any of the terms or coverage of those policies, including amounts paid by
          policyholders.
        </P>,
      ],
    },
    {
      heading: 'Forms',
      body: [
        {
          children: [
            <Clickable to={constants.URLS.THIRD_PARTY.LIFE_INSURANCE_NOMINATION_OF_BENEFICIARIES_ZURICH}>
              Nominations of Beneficiaries form (effective {EFFECTIVE_DATE_MARCH_2024})
            </Clickable>,
            <Clickable to={constants.URLS.THIRD_PARTY.LIFE_INSURANCE_PAYMENT_AUTHORITY_FORM_ZURICH}>
              Payment Authority form (effective {EFFECTIVE_DATE_MARCH_2024})
            </Clickable>,
            <Clickable to={constants.URLS.THIRD_PARTY.LIFE_INSURANCE_CHANGE_OF_ADDRESS_ZURICH}>
              Change of Address form (effective {EFFECTIVE_DATE_MARCH_2024}){' '}
            </Clickable>,
            <Clickable to={constants.URLS.THIRD_PARTY.LIFE_INSURANCE_CHANGE_OF_OWNERSHIP_ZURICH}>
              Change of Ownership form (effective {EFFECTIVE_DATE_MARCH_2024})
            </Clickable>,
            <Clickable to={constants.URLS.THIRD_PARTY.LIFE_INSURANCE_NON_SMOKER_DECLARATION_ZURICH}>
              Non smoker Declaration form (effective {EFFECTIVE_DATE_MARCH_2024})
            </Clickable>,
          ],
        },
      ],
    },
    {
      heading: 'Life Insurance Code of Practice',
      body: [
        <>
          The Life Insurance Code of Practice is the life insurance industry’s commitment to customer service standards
          and it’s designed to protect you, the customer. To learn more,{' '}
          <Clickable to={constants.URLS.THIRD_PARTY.LIFE_INSURANCE_CODE_OF_PRACTICE}>click here.</Clickable>
        </>,
      ],
    },
    {
      heading: 'Need help',
      body: [
        <>
          Call us on{' '}
          <Clickable to={`tel:${LIFE_INSURANCE.NEOS.CONTACT.PHONE.CLAIMS.STANDARD}`}>
            {LIFE_INSURANCE.NEOS.CONTACT.PHONE.CLAIMS.PRETTY}
          </Clickable>{' '}
          between {getOpeningHours(constants.AHM_SERVICE_NAME.LIFE_INSURANCE_ZURICH)}.
        </>,
      ],
    },
  ],
  thingsYouShouldKnow: (
    <>
      <P>
        ahm Life Insurance and ahm Income Protection (collectively ahm life insurance) are issued by the insurer, Zurich
        Australia Limited ABN 92 000 010 195 AFSL 232510 (Zurich). NEOS Direct, a registered business name of NDLI Pty
        Ltd ABN 70 665 747 277 AFSL 547119 distributes and services ahm life insurance which is promoted by ahm health
        insurance (ahm), a business of Medibank Private Limited ABN 47 080 890 259. Medibank Private Limited is an
        authorised representative AR No. 286089 of NEOS Direct.
      </P>
      <P>
        Any advice provided is general only and doesn’t consider your objectives, financial situation or needs. You
        should consider these factors, the appropriateness of the advice, and carefully read the relevant Product
        Disclosure Statement (which sets out the product terms, conditions and exclusions), Target Market Determination
        and Financial Services Guide before making a decision to acquire, or continue to hold, the product.
      </P>
      <P>
        Your personal information will be handled in accordance with the Privacy Policies of the parties involved. Click
        here for{' '}
        <Clickable to={constants.URLS.THIRD_PARTY.NEOS_PRIVACY_POLICY} inNewTab>
          NEOS Direct’s Privacy Policy
        </Clickable>
        , <Clickable to={constants.URLS.SALES.PRIVACY}>ahm’s Privacy Policy</Clickable> or{' '}
        <Clickable to={constants.URLS.THIRD_PARTY.ZURICH_LIFE_PRIVACY_POLICY} inNewTab>
          Zurich’s Privacy Policy.
        </Clickable>
      </P>
    </>
  ),
};

export default lifeAndIncomeContent;
